import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  RefreshCcw,
  Search,
  MessagesSquare,
  Users2,
  Hash,
  MessageSquarePlus,
  Ban,
  Trash2,
  CheckSquare,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Minus,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  accountOfflineReason,
  chatOutgoingStatus,
  chatPreview,
  cn,
  fmtTime,
  initials,
  pendingPreviewText,
  type OutgoingStatus,
} from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import { NewChatDialog } from "@/components/NewChatDialog";
import { useWorkspace } from "@/store/workspace";
import { useEvents } from "@/store/events";
import { useOutbox } from "@/store/outbox";
import type { Account, Chat } from "@/lib/types";

export function ChatList() {
  const { push } = useToast();
  const { selectedAccountId, selectedChatId, selectChat } = useWorkspace();
  const [q, setQ] = useState("");
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const listRef = useRef<HTMLDivElement>(null);

  const { data: chats = [], isFetching } = useQuery({
    queryKey: ["chats", selectedAccountId],
    queryFn: () => api.listChats(selectedAccountId!),
    enabled: !!selectedAccountId,
    refetchInterval: 60_000,
  });

  // Тот же ключ, что у Workspace — берём из кэша, лишнего запроса нет.
  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: api.listAccounts });
  // Всё, что дёргает воркера (refresh, новый чат, блок/удаление), при отключённом
  // аккаунте молча уходит в очередь и не выполняется — гасим кнопки.
  const account = accounts.find((a: Account) => a.id === selectedAccountId);
  const offline = accountOfflineReason(account);
  // Список диалогов из Telegram ещё ни разу не приезжал: пусто — значит «грузим».
  const dialogsPending = !!account && !account.dialogs_synced_at;

  // При смене аккаунта сбрасываем режим выбора и выделение.
  useEffect(() => {
    setSelectMode(false);
    setSelected(new Set());
  }, [selectedAccountId]);

  // Кэш чатов и переписки обновляет useLiveCache; здесь остаются только
  // тосты — массовые операции воркер подтверждает отдельным событием.
  useEffect(() => {
    if (!selectedAccountId) return;
    return useEvents.getState().subscribe(selectedAccountId, (e) => {
      if (e.type === "chats.bulk_blocked") {
        const d = e.data as { blocked?: number; failed?: number; skipped?: number; ok?: boolean };
        push(
          d.ok
            ? {
                title: "Заблокировано",
                description: `Успешно: ${d.blocked ?? 0}${d.skipped ? `, пропущено не приватных: ${d.skipped}` : ""}.`,
                variant: "success",
              }
            : {
                title: "Заблокированы не все",
                description: `Успешно: ${d.blocked ?? 0}, не удалось: ${d.failed ?? 0}. Попробуй ещё раз.`,
                variant: "destructive",
              }
        );
      }
      if (e.type === "chats.bulk_deleted") {
        const d = e.data as { deleted?: number; failed?: number; ok?: boolean };
        push(
          d.ok
            ? {
                title: "Диалоги удалены",
                description: `Удалено: ${d.deleted ?? 0}. Проверено в Telegram.`,
                variant: "success",
              }
            : {
                title: "Удалены не все",
                description: `Удалено: ${d.deleted ?? 0}, не удалось: ${d.failed ?? 0}. Попробуй ещё раз.`,
                variant: "destructive",
              }
        );
      }
    });
  }, [selectedAccountId, push]);

  const refresh = useMutation({
    mutationFn: () => api.refreshChats(selectedAccountId!),
    onSuccess: () => push({ title: "Запрошен refresh", description: "Worker подтянет диалоги." }),
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const bulkBlock = useMutation({
    mutationFn: (ids: string[]) => api.bulkBlockChats(selectedAccountId!, ids),
    onSuccess: () => push({ title: "Блокировка запущена", description: "Дождись подтверждения…" }),
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const bulkDelete = useMutation({
    mutationFn: (ids: string[]) => api.bulkDeleteChats(selectedAccountId!, ids),
    onSuccess: () => push({ title: "Удаление запущено", description: "Дождись подтверждения…" }),
    onError: (e) => push({ title: "Ошибка", description: (e as ApiError).message ?? String(e), variant: "destructive" }),
  });

  const filtered = useMemo(() => {
    let res = chats;
    const t = q.trim().toLowerCase();
    if (t) {
      res = res.filter(
        (c) =>
          (c.title ?? "").toLowerCase().includes(t) ||
          (c.username ?? "").toLowerCase().includes(t)
      );
    }
    return res;
  }, [chats, q]);

  // Список чата держит до 500 строк и перерисовывается на каждое событие —
  // в DOM оставляем только видимые.
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 61,
    getItemKey: (i) => filtered[i]?.id ?? i,
    overscan: 10,
  });

  // Выделение считаем по видимому (отфильтрованному) списку.
  const selectedInView = useMemo(
    () => filtered.filter((c) => selected.has(c.id)),
    [filtered, selected]
  );
  const allSelected = filtered.length > 0 && selectedInView.length === filtered.length;
  const someSelected = selectedInView.length > 0 && !allSelected;
  const privateSelectedCount = selectedInView.filter((c) => c.kind === "private").length;

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach((c) => next.delete(c.id));
      else filtered.forEach((c) => next.add(c.id));
      return next;
    });
  }

  function exitSelect() {
    setSelectMode(false);
    setSelected(new Set());
  }

  function doBlock() {
    const priv = selectedInView.filter((c) => c.kind === "private");
    if (priv.length === 0) {
      push({
        title: "Нет приватных чатов",
        description: "Блокировать можно только личные диалоги.",
        variant: "destructive",
      });
      return;
    }
    const extra = selectedInView.length - priv.length;
    const suffix = extra > 0 ? ` (${extra} не приватных будут пропущены)` : "";
    if (!confirm(`Заблокировать ${priv.length} пользователя(-ей)?${suffix}`)) return;
    bulkBlock.mutate(priv.map((c) => c.id));
    exitSelect();
  }

  function doDelete() {
    const ids = selectedInView.map((c) => c.id);
    if (ids.length === 0) return;
    if (!confirm(`Удалить ${ids.length} диалог(ов)? Диалоги будут удалены у аккаунта в Telegram.`)) return;
    bulkDelete.mutate(ids);
    exitSelect();
  }

  const busy = bulkBlock.isPending || bulkDelete.isPending || !!offline;

  if (!selectedAccountId) {
    return (
      <div className="flex w-80 shrink-0 items-center justify-center border-r border-border text-sm text-muted-foreground">
        Выбери аккаунт слева
      </div>
    );
  }

  return (
    <div className="flex w-80 shrink-0 flex-col border-r border-border bg-card/20">
      <div className="border-b border-border p-3">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Диалоги</h2>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="iconSm"
              onClick={() => setSelectMode((v) => !v)}
              title={selectMode ? "Выйти из режима выбора" : "Выбрать чаты"}
              className={cn(selectMode && "text-primary")}
            >
              <CheckSquare className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="iconSm"
              onClick={() => setNewChatOpen(true)}
              title={offline ? offline : "Начать новый чат"}
              disabled={!!offline}
            >
              <MessageSquarePlus className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="iconSm"
              onClick={() => refresh.mutate()}
              title={offline ? offline : "Запросить refresh у воркера"}
              disabled={refresh.isPending || !!offline}
            >
              <RefreshCcw className={`h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск чата" className="pl-8 h-9" />
        </div>
      </div>

      {selectMode && (
        <div className="flex items-center gap-2 border-b border-border bg-card/40 px-3 py-2">
          <button
            type="button"
            onClick={toggleAll}
            className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
            title={allSelected ? "Снять выделение" : "Выбрать все"}
          >
            <CheckBox checked={allSelected} indeterminate={someSelected} />
            <span>{selectedInView.length > 0 ? `${selectedInView.length} выбрано` : "Выбрать все"}</span>
          </button>
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={doBlock}
              disabled={busy || privateSelectedCount === 0}
              title="Заблокировать выбранных пользователей"
            >
              <Ban className="h-4 w-4" /> Блок
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={doDelete}
              disabled={busy || selectedInView.length === 0}
              title="Удалить выбранные диалоги"
            >
              <Trash2 className="h-4 w-4" /> Удалить
            </Button>
            <Button variant="ghost" size="iconSm" onClick={exitSelect} title="Отмена">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {isFetching && chats.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Загрузка…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <MessagesSquare className="h-8 w-8 opacity-30" />
            {q.trim() && chats.length > 0 ? (
              <span>Ничего не найдено по запросу.</span>
            ) : offline ? (
              <span>
                В базе нет чатов этого аккаунта. Аккаунт не на связи — запросить список
                у Telegram нельзя, пока он не заработает.
              </span>
            ) : dialogsPending ? (
              <span>Загружаем список диалогов из Telegram…</span>
            ) : (
              <span>
                У аккаунта в Telegram нет диалогов. <RefreshCcw className="inline h-3 w-3" /> —
                перепроверить.
              </span>
            )}
          </div>
        ) : (
          <ul className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
            {rowVirtualizer.getVirtualItems().map((v) => {
              const c = filtered[v.index];
              return (
                <ChatRow
                  key={v.key}
                  index={v.index}
                  measure={rowVirtualizer.measureElement}
                  offset={v.start}
                  chat={c}
                  active={c.id === selectedChatId && !selectMode}
                  selectMode={selectMode}
                  checked={selected.has(c.id)}
                  onClick={() => (selectMode ? toggleOne(c.id) : selectChat(c.id))}
                />
              );
            })}
          </ul>
        )}
      </div>

      <NewChatDialog
        accountId={selectedAccountId}
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
      />
    </div>
  );
}

/** Часы/галочка/двойная галочка перед превью последнего исходящего — как в клиенте. */
export function OutgoingStatusIcon({ status }: { status: OutgoingStatus }) {
  switch (status) {
    case "sending":
      return <Clock className="h-3 w-3 shrink-0" />;
    case "failed":
      return <AlertCircle className="h-3 w-3 shrink-0 text-destructive" />;
    case "read":
      return <CheckCheck className="h-3 w-3 shrink-0" />;
    case "sent":
      return <Check className="h-3 w-3 shrink-0" />;
    default:
      return null;
  }
}

function CheckBox({ checked, indeterminate }: { checked: boolean; indeterminate?: boolean }) {
  const on = checked || indeterminate;
  return (
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
        on ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
      )}
    >
      {indeterminate ? <Minus className="h-3 w-3" /> : checked ? <Check className="h-3 w-3" /> : null}
    </span>
  );
}

function ChatRow({
  chat,
  active,
  selectMode,
  checked,
  onClick,
  index,
  offset,
  measure,
}: {
  chat: Chat;
  active: boolean;
  selectMode: boolean;
  checked: boolean;
  onClick: () => void;
  /** Позиция в виртуализированном списке: строка абсолютная, высота измеряется. */
  index: number;
  offset: number;
  measure: (el: HTMLElement | null) => void;
}) {
  const title = chat.title || (chat.username ? `@${chat.username}` : `peer ${chat.tg_peer_id}`);
  const icon =
    chat.kind === "private" ? null : chat.kind === "group" ? <Users2 className="h-3 w-3" /> : <Hash className="h-3 w-3" />;
  const outboxItems = useOutbox((s) => s.items);
  const pending = useMemo(
    () => outboxItems.filter((i) => i.chatId === chat.id),
    [outboxItems, chat.id]
  );
  const status = chatOutgoingStatus(chat, pending);
  const preview = chatPreview(chat, pending[0] ? pendingPreviewText(pending[0]) : null);
  return (
    <li
      data-index={index}
      ref={measure}
      className="absolute left-0 top-0 w-full"
      style={{ transform: `translateY(${offset}px)` }}
    >
      <button
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2.5 text-left transition-colors hover:bg-accent/60",
          active && "bg-accent text-foreground",
          selectMode && checked && "bg-accent/70"
        )}
      >
        {selectMode && <CheckBox checked={checked} />}
        <Avatar
          name={title}
          initials={initials(title.split(" ")[0], title.split(" ")[1], "?")}
          size={36}
          src={chat.avatar_media_id ? api.mediaUrl(chat.avatar_media_id) : null}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 truncate text-sm font-medium">
              {icon}
              {title}
              {chat.peer_blocked_at && (
                <span
                  className="flex h-5 shrink-0 items-center gap-1 rounded bg-muted px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  title={
                    chat.peer_blocked_reason === "deactivated"
                      ? "Собеседник удалил аккаунт — доставка невозможна"
                      : "Собеседник заблокировал аккаунт — доставка невозможна"
                  }
                >
                  <Ban className="h-3 w-3" />
                  {chat.peer_blocked_reason === "deactivated" ? "Удалён" : "Заблок."}
                </span>
              )}
            </span>
            <span className="shrink-0 text-[11px] text-muted-foreground">{fmtTime(chat.last_message_at)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground",
                chat.unread_count > 0 && "text-foreground/80"
              )}
              title={chat.username ? `@${chat.username}` : `id ${chat.tg_peer_id}`}
            >
              <OutgoingStatusIcon status={status} />
              <span
                className={cn(
                  "truncate",
                  // Служебное — событие, а не чья-то реплика: другим цветом,
                  // как в официальном клиенте.
                  chat.last_message?.service && pending.length === 0 && "italic text-primary/80"
                )}
              >
                {preview}
              </span>
            </span>
            {chat.unread_count > 0 && (
              <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                {chat.unread_count}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}
